/**
 * Derive a best-effort display name from an email when the IdP doesn't
 * emit `given_name` / `family_name` / `name` claims.
 *
 * Rules:
 *   - Take the local-part (everything before `@`).
 *   - Split on `.`, `_`, `-`; skip empty chunks.
 *   - Capitalise each chunk's first char, lowercase the rest.
 *   - Join chunks with a single space.
 *   - Fallback to `null` for empty / invalid input so callers can
 *     decide how to degrade (usually "show the email instead").
 *
 * Examples:
 *   "andrey.ognevsky@spaceti.com" → "Andrey Ognevsky"
 *   "ALICE@X.Y"                   → "Alice"
 *   "a.b.c@x.y"                   → "A B C"
 *   "andrey_ognevsky@…"           → "Andrey Ognevsky"
 *   "svc-bot-42@…"                → "Svc Bot 42"
 *   ""                            → null
 */
export function displayNameFromEmail(
  email: string | null | undefined,
): string | null {
  const raw = (email ?? '').trim()
  if (!raw) return null
  const local = raw.split('@')[0] ?? ''
  if (!local) return null
  const parts = local
    .split(/[._-]+/)
    .filter(Boolean)
    .map((chunk) => chunk.charAt(0).toUpperCase() + chunk.slice(1).toLowerCase())
  if (parts.length === 0) return null
  return parts.join(' ')
}

/**
 * Extract up to two initials from a display name. Used for avatar tiles.
 *
 * Falls back to `"·"` (middle dot) for empty input so the avatar never
 * renders a zero-width glyph.
 */
export function initialsFromDisplayName(
  name: string | null | undefined,
): string {
  const raw = (name ?? '').trim()
  if (!raw) return '·'
  const parts = raw.split(/\s+/).filter(Boolean)
  const first = parts[0]?.[0] ?? ''
  const second = parts[1]?.[0] ?? ''
  return (first + second).toUpperCase() || raw[0]!.toUpperCase()
}
